import type { DeploymentRecord } from '@dev-console/types';

export function canRollbackDeployment(item: Pick<DeploymentRecord, 'mode' | 'status'>): boolean {
  return (item.mode === 'runtime' || item.mode === 'infrastructure') && item.status === 'success';
}

export function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rem = minutes % 60;
    return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours === 0 ? `${days}d` : `${days}d ${remHours}h`;
}

/** Prefer total duration; fall back to build+deploy when total is missing. */
export function totalDurationSeconds(
  item: Pick<DeploymentRecord, 'durationSeconds' | 'buildDurationSeconds' | 'deployDurationSeconds'>
): number | null {
  if (item.durationSeconds != null) return item.durationSeconds;
  const build = item.buildDurationSeconds;
  const deploy = item.deployDurationSeconds;
  if (build == null && deploy == null) return null;
  return (build ?? 0) + (deploy ?? 0);
}

export function relativeTime(iso: string | null): string {
  if (iso == null) return '—';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return iso;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return 'just now';
  return `${formatDuration(seconds)} ago`;
}

export function formatClock(iso: string | null): string {
  if (iso == null) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
